import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Project } from '../../../../types/graphqlTypes';
import FilterIcon from '../../../../assets/icons/filter.svg?react';
import { AnnotationContextGroup, collectLeafAnnotations } from '../../../../contexts/AnnotationContext';
import { FILTERABLE_ANNOTATION_TYPES } from '../../../../utils/constants';
import { useReactivePropertyManager } from '../../../Properties/reactive/useReactivePropertyManager';
import { PropertyType } from '../../../Properties/types';
import { ensureFilterTogglePropertyRegistered } from '../../../Properties/integration/annotations/FilterToggleProperty';

const TOGGLE_OBJECT_ID = 'toggles';

const GroupFilterIcon_: React.FC2<{
  project?: Pick<Project, 'id'>;
  group?: AnnotationContextGroup;
}> = ({ project, group }) => {
  const projectId = project?.id || '';
  const service = useReactivePropertyManager({ projectId });

  // Get annotations that can be filtered — leaves only, descending into
  // nested room folders (a folder node is never itself filterable).
  const filterableAnnotations = useMemo(() => {
    if (!group) return [];
    return collectLeafAnnotations(group).filter((annotation) => FILTERABLE_ANNOTATION_TYPES.includes(annotation.type));
  }, [group]);

  // Determine if group has any filterable annotations
  const hasFilterableAnnotations = filterableAnnotations.length > 0;

  // Ensure all filter properties are registered
  for (const annotation of filterableAnnotations) {
    ensureFilterTogglePropertyRegistered(annotation.identifier);
  }

  // Subscribe to all filter toggle properties and track aggregate state
  const [isAnyFilterEnabled, setIsAnyFilterEnabled] = useState(false);

  useEffect(() => {
    if (filterableAnnotations.length === 0) return;

    const properties = filterableAnnotations.map((annotation) => {
      const propertyIdentifier = `filter.${annotation.identifier}`;
      return service.getProperty(propertyIdentifier, TOGGLE_OBJECT_ID, PropertyType.PROJECT_USER);
    });

    const checkAnyEnabled = () => {
      setIsAnyFilterEnabled(properties.some((p) => p.value === true));
    };

    // Initial check
    checkAnyEnabled();

    // Subscribe to all properties
    const subscriptions = properties.map((property) => property.subscribe(checkAnyEnabled));

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [filterableAnnotations, service]);

  const onToggleGroupFilter = useCallback(() => {
    if (!project || !group || filterableAnnotations.length === 0) return;

    const shouldEnable = !isAnyFilterEnabled;

    for (const annotation of filterableAnnotations) {
      const propertyIdentifier = `filter.${annotation.identifier}`;
      service.setValue(propertyIdentifier, TOGGLE_OBJECT_ID, PropertyType.PROJECT_USER, shouldEnable);
    }
  }, [project, group, filterableAnnotations, isAnyFilterEnabled, service]);

  // Don't render if no filterable annotations
  if (!hasFilterableAnnotations) return null;

  return (
    <div onClick={onToggleGroupFilter} className="flex items-center justify-center w-6 h-6 cursor-pointer relative">
      <FilterIcon className={`w-4 h-4 stroke-2 ${isAnyFilterEnabled ? 'text-green-500 hover:text-green-600' : 'text-[#6F7173] hover:text-black dark:hover:text-neon-green-300'}`} />
    </div>
  );
};

export const GroupFilterIcon = memo(GroupFilterIcon_);
